import { engineComponent } from "neo-common";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { Symbols } from "../Symbols";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { StageController } from "../../Components/StageController";
import { GameManager } from "../../Managers/GameManager";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components//Effects/Tweens";
import { ShapeElement } from "../../Components/Elements/Graphics/ShapeElement";


import { ModuleManager } from "../../Managers/ModuleManager";
import { DataManager } from "../../Managers/DataManager";
import { WinAnimator2 } from "../../CustomComponents/WinAnimator2";
import { WinAnimator } from "../WinAnimator";
import { LayoutManager } from "../../Managers/LayoutManager";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { SceneHolderElement } from "../../CustomComponents/SceneHolderElement";
import { LayoutSwitchElement } from "../../CustomComponents/LayoutSwitchElement";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends LayoutSwitchElement {
    public static LayoutManager: LayoutManager;

    public static Background: SwitchElement;
    public static Logo: SwitchElement;
    public static Transition: SwitchElement;

    private _controller: BaseController;

    private _wasFinished: boolean = false;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimator2;
    private _noWinContainer: ContainerElement;
    private _winContainer: ContainerElement;

    private _teasingBottom: SwitchElement;
    private _teasingTop: SwitchElement;

    private _transition: SwitchElement;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        StageController.baseStageContainer = this;
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        BaseView.LayoutManager = new LayoutManager();

        this._paylines = this.getChildElementByName("Paylines") as Paylines;
        this._paylines.addTopContainer(this.getChildElementByName("PaylinesTop") as ContainerElement);
        this._symbols = this.getChildElementByName("Symbols") as Symbols;
        this._machine = this.getChildElementByName("Machine") as MachineExtended;
        this._winAnimator = this.getChildElementByName("WinAnimator") as WinAnimator2;
        this._noWinContainer = this.getChildElementByName("NoWinContainer") as ContainerElement;
        this._winContainer = this.getChildElementByName("WinContainer") as ContainerElement;
        this._teasingBottom = this.getChildElementByName("TeasingBottom") as SwitchElement;
        this._teasingTop = this.getChildElementByName("TeasingTop") as SwitchElement;
        BaseView.Background = this.getChildElementByName("Background") as SwitchElement;
        BaseView.Logo = this.getChildElementByName("Logo") as SwitchElement;
        BaseView.Transition = (this.getChildElementByName("TransitionHolder") as ContainerElement).childs[
            "Transition"
        ] as SwitchElement;
    }

    disableLogo(): void {
        //ToDo do we still need this ?
        /*if (this.getChildElementByName('Logo') != null) {
            this.removeChild(this.getChildElementByName('Logo'));
        }*/
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this._teasingBottom,
                            this._teasingTop,
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this._noWinContainer,
                                this._winContainer,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }

                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        if (ModuleManager.gambleActive()) {
            this.goToCase("GAMBLE_INIT");
        } else if (ModuleManager.pickActive()) {
            this.goToCase("PICK_INIT");
        } else if (ModuleManager.freespinsActive()) {
            this.goToCase("FS_INIT");
        }

        if (ModuleManager.gambleActive()) {
            if (BaseView.Background != null && BaseView.Background.getCurrentCase() === "BASE") {
                BaseView.Background.goToCase("BASE_WIN");
            }
        }
    }

    initIntro(): void {
        this.goToCase("BASE_INIT");
        if (BaseView.Logo != null && BaseView.Logo.getCurrentCase() !== "NORMAL") {
            BaseView.Logo.goToCase("NORMAL");
        }
        if (BaseView.Background != null) {
            BaseView.Background.goToCase("BASE");
        }

        if (GameManager.baseResponse.currentWin > 0) {
            if (BaseView.Background != null && BaseView.Background.getCurrentCase() === "BASE") {
                BaseView.Background.goToCase("BASE_WIN");
            }
        }

        StageController.baseStage = true;
        StageController.freespinStage = false;

        if (GameManager.gameResponse.totalWin > 0 || ModuleManager.freespinsEnabled()) {
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        if (BaseView.Background != null && BaseView.Background.getCurrentCase() === "BASE_WIN") {
            BaseView.Background.goToCase("TO_BASE");
        }

        if (GameManager.gameResponse !== null && GameManager.gameResponse.totalWin > 0) this._winAnimator.reset(true);
    }

    spin(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        if (BaseView.Background != null && BaseView.Background.getCurrentCase() === "BASE_WIN") {
            BaseView.Background.goToCase("TO_BASE");
        }

        this._machine.spinStart(this._winAnimator.reset(true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    machineStopped(): void {
        this._wasFinished = false;

        this._controller.machineStopped();

        if (
            GameManager.baseResponse.currentWin > 0 &&
            BaseView.Background != null &&
            BaseView.Background.getCurrentCase() === "BASE"
        ) {
            BaseView.Background.goToCase("BASE_WIN");
        }

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    finishWinCalculation(): void {
        this._wasFinished = true;

        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay || ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        this.goToCase("BASE_INIT");
        if (BaseView.Logo != null && BaseView.Logo.getCurrentCase() !== "NORMAL") {
            BaseView.Logo.goToCase("NORMAL");
        }

        if (BaseView.Background != null) {
            BaseView.Background.goToCase("BASE");
        }
        StageController.baseStage = true;

        if (long) {
            (<ContainerElement>this.getChildElementByName("TransitionHolder")).addChild(BaseView.Transition);
            BaseView.Transition.goToCase("END");
            this.visible = true;

            this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);

            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
            this._winAnimator.reset(true);
            this._introDelay.start(40);
        } else {
            this.goToCase("GAMBLE_END");
            this._introDelay.start(10);
            // this.introFinished();
        }
    }

    introFinished(): void {
        if (BaseView.Background != null) {
            BaseView.Background.goToCase("BASE");
        }
        StageController.baseStage = true;

        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop");
        }

        this._controller.introFinished();
    }

    outro(): void {
        StageController.baseStage = true;

        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");

        if (ModuleManager.pickActive()) {
            this.outroToPick();
            /*} else if(ModuleManager.freespinsActive()) {
                this.outroToFreespins();*/
        } else {
            this.outroToGamble();
        }
    }

    private outroToPick(): void {
        this.goToCase("PICK_START");
        if (BaseView.Logo != null && BaseView.Logo.getCurrentCase() === "NORMAL") {
            BaseView.Logo.goToCase("PICK_START");
        }

        PlayList.play("SND_BonusWin");

        this._winAnimator.outroToPick();
        this._outroDelay.start(40);
    }

    private outroToFreespins(): void {
        if (this._paylines != null) {
            this._paylines.alpha = 0;
        }

        // this.goToCase('FS_START');

        if (this._transition != null) {
            this._transition.goToCase("START");
        }

        PlayList.play("SND_FSTransition");

        this._outroDelay.start(120);
    }

    private outroToGamble(): void {
        const inHighWins = this._winAnimator.reset(true);
        let transitionDelay = 0;
        let outroDelay = 10;
        if (this._wasFinished) {
            this.setDynamicTime(transitionDelay);
            this.goToCase("GAMBLE_START");
            this._outroDelay.start(transitionDelay + outroDelay);
        } else {
            if (inHighWins) {
                transitionDelay = 60;
                this.setDynamicTime(transitionDelay);
                this.goToCase("GAMBLE_START");
                this._outroDelay.start(transitionDelay + outroDelay);
            } else {
                transitionDelay = 30;
                this.setDynamicTime(transitionDelay);
                this.goToCase("GAMBLE_START");
                this._outroDelay.start(transitionDelay + outroDelay);
            }
        }
    }

    outroFinished(): void {
        StageController.baseStage = true;

        this._controller.outroFinished();
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    resetScreen(): void {
        if (this._winAnimator) {
            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
        }

        if (this._machine) {
            this._machine.spinReset();
            this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        }

        const node = document.createElement("ResetOverlay");
        node.setAttribute("width", StageController.Width.toString());
        node.setAttribute("height", StageController.Height.toString());
        node.setAttribute("color", "000000");
        const resetOverlay = new ShapeElement();
        resetOverlay.create(node);

        this.addChild(resetOverlay);
        Tweens.removeTweens(resetOverlay, "alpha");
        Tweens.alphaTween(resetOverlay, {
            alphas: [1, 0],
            tweenDuration: 10,
            callbackFunction: () => {
                resetOverlay.destroy();
            },
        });

        PIXI.sound.unmuteAll();
        PlayList.stopAll();

        this.initIntro();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/